import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CustomerPayment, Supplier } from '../types';
import { formatDate } from '../utils/dateFormat';
import AccountTypeSelector from '../components/AccountTypeSelector';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

const CustomerPayments: React.FC = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState<CustomerPayment[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState<CustomerPayment | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [openedViaQuery, setOpenedViaQuery] = useState(false);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [selectedJsonData, setSelectedJsonData] = useState<any>(null);

  const [formData, setFormData] = useState({
    supplierId: '',
    amount: '',
    method: 'CASH',
    comment: '',
    status: 'DRAFT' as 'DRAFT' | 'POSTED' | 'CANCELLED',
    date: new Date().toISOString().split('T')[0],
    accountId: '',
  });

  useEffect(() => {
    fetchPayments();
    fetchSuppliers();
    fetchAccounts();
  }, []);

  // Открытие на редактирование по query ?open=<id>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('open');
    if (!openId) return;
    setOpenedViaQuery(true);
    (async () => {
      try {
        const payment = await apiGet<CustomerPayment>(`/customer-payments/${openId}`);
        setEditingPayment(payment);
        setFormData({
          supplierId: payment.supplierId,
          amount: payment.amount.toString(),
          method: payment.method,
          comment: payment.comment || '',
          status: payment.status,
          date: payment.date ? payment.date.split('T')[0] : new Date().toISOString().split('T')[0],
          accountId: payment.accountId || ''
        });
        setShowForm(true);
      } catch (e) {
        console.error('Error opening payment:', e);
      }
    })();
  }, []);

  // Предзаполнение данных из продажи ТМЦ
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const createFrom = params.get('createFrom');
    const saleType = params.get('saleType');
    const supplierId = params.get('supplierId');
    const amount = params.get('amount');
    const description = params.get('description');
    const date = params.get('date');

    if (createFrom && saleType === 'inventory' && supplierId && amount) {
      setFormData({
        supplierId: supplierId,
        amount: amount,
        method: 'CASH',
        comment: description || '',
        status: 'DRAFT',
        date: date || new Date().toISOString().split('T')[0],
        accountId: ''
      });
      setShowForm(true);
    }
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const data = await apiGet<{ payments?: CustomerPayment[] }>('/customer-payments');
      setPayments(data.payments || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const data = await apiGet<{ suppliers?: Supplier[] }>('/suppliers?limit=1000');
      setSuppliers(data.suppliers || []);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  };

  const fetchAccounts = async () => {
    try {
      const data = await apiGet<any[]>('/cash-accounts');
      setAccounts(data);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Автоматически определяем способ оплаты на основе выбранного счета
      const selectedAccount = accounts.find(account => account.id === formData.accountId);
      const paymentMethod = selectedAccount ? selectedAccount.type : 'CASH';
      
      const payload = {
        ...formData,
        method: paymentMethod
      };

      if (editingPayment) {
        await apiPut(`/customer-payments/${editingPayment.id}`, payload);
      } else {
        await apiPost('/customer-payments', payload);
      }

      await fetchPayments();
      setShowForm(false);
      setEditingPayment(null);
      setFormData({
        supplierId: '',
        amount: '',
        method: 'CASH',
        comment: '',
        status: 'DRAFT',
        date: new Date().toISOString().split('T')[0],
        accountId: ''
      });
      
      // Если открыли через query, закрываем форму и убираем query
      if (openedViaQuery) {
        navigate('/customer-payments');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save payment');
    }
  };

  const handleEdit = (payment: CustomerPayment) => {
    setEditingPayment(payment);
    setFormData({
      supplierId: payment.supplierId,
      amount: payment.amount.toString(),
      method: payment.method,
      comment: payment.comment || '',
      status: payment.status,
      date: payment.date ? payment.date.split('T')[0] : new Date().toISOString().split('T')[0],
      accountId: payment.accountId || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Вы уверены, что хотите удалить эту оплату?')) return;
    
    try {
      await apiDelete(`/customer-payments/${id}`);
      await fetchPayments();
    } catch (err: any) {
      setError(err.message || 'Failed to delete payment');
    }
  };

  const handleNewPayment = () => {
    setEditingPayment(null);
    setFormData({
      supplierId: '',
      amount: '',
      method: 'CASH',
      comment: '',
      status: 'DRAFT',
      date: new Date().toISOString().split('T')[0],
      accountId: ''
    });
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingPayment(null);
    setFormData({
      supplierId: '',
      amount: '',
      method: 'CASH',
      comment: '',
      status: 'DRAFT',
      date: new Date().toISOString().split('T')[0],
      accountId: ''
    });
    
    // Если открыли через query, убираем query
    if (openedViaQuery) {
      navigate('/customer-payments');
    }
  };

  const showJson = (payment: CustomerPayment) => {
    setSelectedJsonData(payment);
    setShowJsonModal(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'POSTED': return 'text-green-600 bg-green-100';
      case 'CANCELLED': return 'text-red-600 bg-red-100';
      default: return 'text-yellow-600 bg-yellow-100';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'POSTED': return 'Проведен';
      case 'CANCELLED': return 'Отменен';
      default: return 'Черновик';
    }
  };

  const getAccountTypeLabel = (type: string) => {
    const typeLabels: { [key: string]: string } = {
      'CASH': 'Касса',
      'BANK': 'Банк',
      'CARD': 'Карта',
      'EXTERNAL': 'Внешний'
    };
    return typeLabels[type] || type;
  };

  const getAccountTypeColor = (type: string) => {
    const typeColors: { [key: string]: string } = {
      'CASH': 'bg-green-100 text-green-800',
      'BANK': 'bg-blue-100 text-blue-800',
      'CARD': 'bg-purple-100 text-purple-800',
      'EXTERNAL': 'bg-gray-100 text-gray-800'
    };
    return typeColors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Оплаты покупателей</h1>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/inventory-sales')}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
          >
            Продажи ТМЦ
          </button>
          <button
            onClick={handleNewPayment}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Создать оплату
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Номер
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Дата
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Покупатель
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Сумма
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Способ
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Счет
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Статус
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {payments.map((payment) => (
              <tr key={payment.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {payment.number}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(payment.date)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {payment.supplier?.name || 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {payment.amount.toFixed(2)} MDL
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {payment.method === 'CASH' ? 'Наличные' : 'Карта'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {payment.account ? (
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-900">{payment.account.name}</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAccountTypeColor(payment.account.type)}`}>
                        {getAccountTypeLabel(payment.account.type)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-400">Не указан</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                    {getStatusText(payment.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handleEdit(payment)}
                      className="text-blue-600 hover:text-blue-900 text-sm"
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => showJson(payment)}
                      className="text-gray-600 hover:text-gray-900 text-sm"
                      title="Показать JSON"
                    >
                      📄
                    </button>
                    <button
                      onClick={() => handleDelete(payment.id)}
                      className="text-red-600 hover:text-red-900 text-sm"
                      title="Удалить"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Форма создания/редактирования */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingPayment ? 'Редактировать оплату' : 'Создать оплату'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Покупатель *
                  </label>
                  <select
                    value={formData.supplierId}
                    onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  >
                    <option value="">Выберите покупателя</option>
                    {suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Дата *
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Сумма *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <AccountTypeSelector
                    value={formData.accountId}
                    onChange={(accountId) => setFormData({ ...formData, accountId })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Статус
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="DRAFT">Черновик</option>
                    <option value="POSTED">Проведен</option>
                    <option value="CANCELLED">Отменен</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Комментарий
                  </label>
                  <textarea
                    value={formData.comment}
                    onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    rows={3}
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingPayment ? 'Сохранить' : 'Создать'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* JSON Modal */}
      {showJsonModal && selectedJsonData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-3/4 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                JSON данные оплаты
              </h3>
              <pre className="bg-gray-100 p-4 rounded-md overflow-auto max-h-96">
                {JSON.stringify(selectedJsonData, null, 2)}
              </pre>
              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setShowJsonModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  Закрыть
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerPayments;
